<script setup lang="ts">
/**
 * Step Récapitulatif du checkout adhésion :
 * liste adhérents, détails plan/options, code promo, total,
 * fees / soutien Donoor / breakdown, zone Stripe + PayPal,
 * mention des conditions (acceptées en validant), bouton submit.
 */
import type { useCheckout } from "@donoor/checkout";
import type { MembershipPlanPublic, MembershipPlanOption } from "~/types/membership";
import { nomDuTarif } from "~/utils/tarifAdhesion";
import { echeancierDuPanier, jourCivilParis } from "@donoor/utils";
import { fraisDesParts, suiteIdentique } from "~/utils/plusieursFois";
import PublicPageTotauxPlusieursFois from "~/components/public-page/PublicPageTotauxPlusieursFois.vue";

const { formatCurrency } = useFormatLangue();
const { t } = useI18n();

interface ParticipantLocal {
  planId: string;
  planTitle: string;
  planPrice: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  selectedOptions: Record<string, boolean>;
  customFieldValues: Record<string, unknown>;
}

type SiblingPlan = MembershipPlanPublic & {
  options?: MembershipPlanOption[];
};

interface CalculationsView {
  feesAmount: { value: number };
  finalAmount: { value: number };
  amountWithFees: { value: number };
  formatAmount: (a: number) => string;
}

interface PromoApplied {
  code: string;
  discount_type: string;
  discount_value: number;
}

const props = defineProps<{
  plan: MembershipPlanPublic;
  participants: ParticipantLocal[];
  hasMultiplePlans: boolean;
  allPlans: SiblingPlan[];
  planQuantities: Record<string, number>;
  quantity: number;
  totalPlanPrice: number;
  totalOptionsPrice: number;
  subtotal: number;
  totalPrice: number;
  promoCode: string;
  promoLoading: boolean;
  promoError: string;
  promoApplied: PromoApplied | null;
  promoDiscount: number;
  /** true dès qu'il reste quelque chose à encaisser — y compris quand SEUL le
   *  soutien Donoor subsiste (adhésion gratuite ou remisée à 100 %). */
  needsPayment: boolean;
  /**
   * Aperçu depuis le dashboard : le tunnel s'affiche pour que l'organisation
   * voie sa configuration, mais AUCUN paiement ne doit pouvoir partir.
   * Karim 2026-09-10 — parité billetterie et boutique, où c'était déjà le cas.
   */
  isPreview?: boolean;
  requiresApproval: boolean;
  /** Bloc « Faire un don » : réglage « Don libre » du tarif (migration 291). */
  showDonation: boolean;
  showDonationInput: boolean;
  /** Don à l'association, en euros. */
  donationAmount: number;
  includeFees: boolean;
  donateUs: boolean;
  donateToUsAmount: number;
  hasFees: boolean;
  calculations: CalculationsView;
  /** Instances useCheckout pour les composants Stripe/PayPal (Express + Card). */
  stripeCheckout: ReturnType<typeof useCheckout>;
  paypalCheckout: ReturnType<typeof useCheckout>;
  /** Slice du paymentForm pour DonateExpressCheckoutSection (isSubscription). */
  paymentForm: { isSubscription: Ref<boolean> };
  /** Provider primaire (Karim 2026-06-21). */
  usesPaypalCard: boolean;
  usesPaypalWallets: boolean;
  /** PayPal-only (Stripe pas configuré). */
  isPaypalOnlyMode: boolean;
  /** Bouton PayPal alternatif visible (les 2 actifs). */
  showPayPalAlternative: boolean;
  stripeChargesEnabled: boolean;
  paypalChargesEnabled: boolean;
  stripeInitError: boolean;
  stripeErrorMessage: string;
  checkoutIsInitialized: boolean;
  checkoutError: unknown;
  orderError: string | null;
  orderErrorTechnical: boolean;
  isProcessing: boolean;
  getParticipantPrice: (p: ParticipantLocal) => number;
  /** Paiement en plusieurs fois : réglage du tarif, nombre choisi, commission (décimal). */
  reglagePlusieursFois?: unknown;
  /** Fin de l'adhésion la plus courte du panier (`null` = sans limite). */
  dernierJourPlusieursFois?: string | null;
  nombreParts?: number;
  tauxCommission?: number;
}>();

defineEmits<{
  (e: 'update:promo-code', v: string): void;
  (e: 'apply-promo' | 'remove-promo' | 'retry-stripe-init' | 'clear-order-error' | 'back' | 'submit'): void;
  (e: 'update:include-fees' | 'update:show-donation-input', v: boolean): void;
  (e: 'update:donate-to-us-amount' | 'update:donation-amount' | 'update:nombre-parts', v: number): void;
}>();

/** Montant à payer en une fois (frais couverts ou non). */
const totalUneFois = computed(() =>
  props.includeFees
    ? props.calculations.amountWithFees.value
    : props.totalPrice + props.donationAmount + (props.donateUs ? props.donateToUsAmount : 0),
);

/**
 * Montant débité AUJOURD'HUI : la 1re part en plusieurs fois (même calcul que
 * le serveur), sinon le total.
 */
/** Parts du paiement en plusieurs fois choisi (vide en une fois), calcul du serveur. */
const partsChoisies = computed(() => {
  const n = props.nombreParts ?? 0;
  if (n < 2) return [];
  return echeancierDuPanier({
      montantDiviseEuros: props.totalPrice,
      nombre: n,
      premierJour: jourCivilParis(),
      enPlusEuros: props.donationAmount,
      soutienEuros: props.donateUs ? props.donateToUsAmount : 0,
      tauxCommission: props.tauxCommission ?? 0,
      fraisCouverts: props.includeFees,
    });
});

const aPayerAujourdhui = computed(() => partsChoisies.value[0]?.montant ?? totalUneFois.value);

/** Frais affichés : en plusieurs fois, ceux de TOUTE la commande (un par paiement). */
const fraisAffiches = computed(() =>
  partsChoisies.value.length >= 2
    ? fraisDesParts(partsChoisies.value, props.totalPrice + props.donationAmount + (props.donateUs ? props.donateToUsAmount : 0))
    : props.calculations.feesAmount.value,
);

/** « puis 2 × 20 € » (ou « puis 2 prélèvements mensuels ») sous le bouton Payer. */
const suitePaiement = computed(() => {
  const parts = partsChoisies.value;
  if (parts.length < 2) return "";
  const suite = suiteIdentique(parts);
  return suite
    ? t("plusieursFois.choix.puisNFois", { n: suite.nombre, montant: formatCurrency(suite.montant) })
    : t("plusieursFois.choix.puisNPrelevements", { n: parts.length - 1 });
});

/** Widget intégré chez l'association (`?embed=1`) : pas de colonne de droite. */
const routeRecap = useRoute();
const estIntegre = computed(() => routeRecap.query.embed === "1" || routeRecap.query.embed === "true");
</script>

<template>
  <div class="p-3 md:p-5">
    <PublicPageOrderErrorBanner
      v-if="orderError"
      :error="orderError"
      :technical="orderErrorTechnical"
      :contact-email="plan?.contact_email"
      @clear="$emit('clear-order-error')"
    />

    <!-- Récapitulatif fusionné : adhérent(s) + détails plan/options/promo -->
    <div class="rounded-2xl border border-gray-200 dark:border-[#262626] overflow-hidden mb-4">
      <div class="px-5 py-3 bg-gray-50 dark:bg-[#0f0f0f] border-b border-gray-100 dark:border-[#1e1e1e]">
        <p class="text-xs font-semibold text-[#717198] dark:text-gray-500 uppercase tracking-wide">{{ $t('adhesion.aslugStepRecap.recapitulatif') }}</p>
      </div>
      <div class="divide-y divide-gray-100 dark:divide-[#1e1e1e]">
        <!-- Adhérent(s) : nom + email (pas de prix ici — porté par la ligne
             plan en-dessous pour éviter le doublon visuel). -->
        <div v-for="(p, idx) in participants" :key="idx" class="px-5 py-3">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-full bg-gray-100 dark:bg-[#1e1e1e] flex items-center justify-center shrink-0">
              <span v-if="participants.length > 1" class="text-xs font-bold text-[#505870] dark:text-gray-400">{{ idx + 1 }}</span>
              <UIcon v-else name="i-lucide-user" class="size-4 text-[#505870] dark:text-gray-400" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-[#2d2e5c] dark:text-white truncate">
                {{ p.firstName }} {{ p.lastName }}
              </p>
              <p class="text-xs text-[#717198] dark:text-gray-500 truncate">
                <span v-if="hasMultiplePlans">{{ p.planTitle }} — </span>{{ p.email }}
              </p>
            </div>
          </div>
        </div>

        <!-- Plan(s) : toujours affiché — porte le prix. -->
        <template v-if="hasMultiplePlans">
          <div
            v-for="ap in allPlans.filter((p) => (planQuantities[p.id] || 0) > 0)"
            :key="ap.id"
            class="flex items-center justify-between px-5 py-3 text-sm"
          >
            <div class="flex items-center gap-2">
              <UIcon name="i-lucide-id-card" class="size-4 text-[#717198]" />
              <span class="text-[#2d2e5c] dark:text-white">{{ nomDuTarif(ap) }} {{ (planQuantities[ap.id] || 0) > 1 ? `× ${planQuantities[ap.id]}` : '' }}</span>
            </div>
            <span class="font-semibold text-[#2d2e5c] dark:text-white">
              {{ formatCurrency(Number(ap.price) * (planQuantities[ap.id] || 0)) }}
            </span>
          </div>
        </template>
        <div v-else class="flex items-center justify-between px-5 py-3 text-sm">
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-id-card" class="size-4 text-[#717198]" />
            <span class="text-[#2d2e5c] dark:text-white">{{ plan.title }} {{ quantity > 1 ? `× ${quantity}` : '' }}</span>
          </div>
          <span class="font-semibold text-[#2d2e5c] dark:text-white">
            {{ totalPlanPrice === 0 ? $t('adhesion.aslugStepRecap.gratuit') : formatCurrency(totalPlanPrice) }}
          </span>
        </div>

        <!-- Options -->
        <div v-if="totalOptionsPrice > 0" class="flex items-center justify-between px-5 py-3 text-sm">
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-plus-circle" class="size-4 text-[#717198]" />
            <span class="text-[#717198] dark:text-gray-400">{{ $t('adhesion.aslugStepRecap.options') }}</span>
          </div>
          <span class="font-medium text-[#505870] dark:text-gray-400">
            +{{ formatCurrency(totalOptionsPrice) }}
          </span>
        </div>

        <!-- Code promo -->
        <div v-if="promoDiscount > 0" class="flex items-center justify-between px-5 py-3 text-sm">
          <div class="flex items-center gap-2">
            <UIcon name="i-lucide-tag" class="size-4 text-emerald-500" />
            <span class="text-emerald-600 dark:text-emerald-400">{{ $t('adhesion.aslugStepRecap.codePromo', { code: promoApplied?.code }) }}</span>
          </div>
          <span class="font-medium text-emerald-600 dark:text-emerald-400">
            -{{ formatCurrency(promoDiscount) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Promo code input déplacé en sidebar droite (parité /b/[slug]). -->

    <!-- Don à l'association (migration 291) : réglage « Don libre » du tarif.
         Débité avec l'adhésion, sans commission Donoor ; seule part ouvrant
         droit à un reçu fiscal quand le tarif ne l'est pas. Une DEMANDE
         d'adhésion le propose au paiement, sur son lien, comme le soutien. -->
    <div v-if="showDonation && !requiresApproval" class="mt-5 rounded-2xl border border-gray-200 dark:border-[#262626] p-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <UIcon name="i-lucide-heart" class="size-4 text-(--color-main)" />
          <span class="text-sm font-medium text-[#2d2e5c] dark:text-gray-300">
            {{ $t('adhesion.aslugStepRecap.faireDonA', { organisation: plan.organization_name || $t('adhesion.aslugStepRecap.organisationParDefaut') }) }}
          </span>
        </div>
        <USwitch
          :model-value="showDonationInput"
          size="xs"
          :ui="{ base: 'data-[state=checked]:bg-(--color-main)!' }"
          @update:model-value="(v: boolean) => {
            $emit('update:show-donation-input', v);
            if (v && donationAmount === 0) $emit('update:donation-amount', 5);
            if (!v) $emit('update:donation-amount', 0);
          }"
        />
      </div>
      <Transition enter-active-class="transition-all duration-300 ease-out" enter-from-class="opacity-0 max-h-0" enter-to-class="opacity-100 max-h-32" leave-active-class="transition-all duration-200 ease-in" leave-from-class="opacity-100 max-h-32" leave-to-class="opacity-0 max-h-0">
        <div v-if="showDonationInput" class="overflow-hidden mt-3 space-y-2">
          <p class="text-xs text-[#717198] dark:text-gray-500">{{ $t('adhesion.aslugStepRecap.donLibreTexte') }}</p>
          <!-- Grille de 4 colonnes égales (Karim 2026-09-26) : les montants
               en `flex-1` et « Autre » en `w-24` laissaient trop peu de place
               pour saisir un montant. -->
          <div class="grid grid-cols-4 gap-2">
              <button v-for="preset in [5, 10, 20]" :key="preset" type="button" class="py-2 rounded-xl text-sm font-medium border transition-colors" :class="donationAmount === preset ? 'accent-btn border-transparent' : 'border-gray-200 dark:border-[#262626] text-[#2d2e5c] dark:text-gray-300 hover:border-gray-300'" @click="$emit('update:donation-amount', donationAmount === preset ? 0 : preset)">
                {{ preset }} €
              </button>
            <div class="relative min-w-0">
              <input
                id="adhesion-don-libre"
                :value="donationAmount"
                type="number"
                min="0"
                step="1"
                :placeholder="$t('adhesion.aslugStepRecap.autrePlaceholder')"
                :aria-label="$t('adhesion.aslugStepRecap.montantDonAriaLabel')"
                class="w-full pl-3 pr-7 py-2 rounded-xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#141414] text-sm text-right font-medium text-[#2d2e5c] dark:text-white focus:outline-none focus:border-[#212121] dark:focus:border-white transition-colors hide-arrows"
                @input="$emit('update:donation-amount', Math.max(0, Number(($event.target as HTMLInputElement).value) || 0))"
              />
              <span class="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">€</span>
            </div>
          </div>
        </div>
      </Transition>
    </div>

    <!-- Payment section -->
    <!-- Soutenir Donoor — TOUJOURS visible (règle produit) : même une adhésion
         gratuite ou remisée à 100 % doit pouvoir soutenir Donoor. Parité
         /b/[slug]. Karim 2026-08-07. -->
    <div v-if="!requiresApproval" class="mt-5 rounded-2xl border border-gray-200 dark:border-[#262626] p-4">
      <DonateSupportDonoor
        :donate-to-us-amount="donateToUsAmount"
        :format-amount="calculations.formatAmount"
        :organization-name="plan?.organization_name ?? undefined"
        @update:donate-to-us-amount="$emit('update:donate-to-us-amount', $event)"
      />
    </div>

    <template v-if="needsPayment && !requiresApproval">
      <!-- Fees option -->
      <div class="mt-3 rounded-2xl border border-gray-200 dark:border-[#262626] p-4">
        <DonateFeesOption
          :include-fees="includeFees"
          :fees-amount="calculations.feesAmount.value"
          :final-amount="calculations.finalAmount.value"
          :format-amount="calculations.formatAmount"
          :fees-text="hasFees ? 'Stripe et Donoor' : 'Stripe'"
            :provider-label="usesPaypalCard ? 'paypal' : 'stripe'"
          @update:include-fees="$emit('update:include-fees', $event)"
        />
      </div>

      <!-- Paiement en plusieurs fois (Karim 2026-10-21) : carte uniquement. -->
      <ChoixPaiementPlusieursFois
        :reglage="reglagePlusieursFois"
        :dernier-jour="dernierJourPlusieursFois ?? null"
        :montant-divise="totalPrice"
        :en-plus="donationAmount"
        :soutien="donateUs ? donateToUsAmount : 0"
        :taux-commission="tauxCommission ?? 0"
        :frais-couverts="includeFees"
        :total-une-fois="totalUneFois"
        :indisponible="usesPaypalCard || isPaypalOnlyMode || isPreview"
        :model-value="nombreParts ?? 0"
        @update:model-value="$emit('update:nombre-parts', $event)"
      />

      <!-- Code promo : sur ordinateur il est déjà dans le détail à droite
           (Karim 2026-10-21) — affiché ici seulement sur téléphone (le
           détail de droite y est masqué) et dans le widget intégré (pas de
           colonne de droite). -->
      <div v-if="subtotal > 0" class="mt-3" :class="estIntegre ? '' : 'lg:hidden'">
        <PublicPagePromoCodeInput
          variant="a"
          :model-value="promoCode"
          :applied="promoApplied"
          :loading="promoLoading"
          :error="promoError"
          @update:model-value="$emit('update:promo-code', $event)"
          @apply="$emit('apply-promo')"
          @remove="$emit('remove-promo')"
        />
      </div>

      <!-- Amount breakdown -->
      <!-- Détail des montants : sur ordinateur il est déjà dans la colonne de
           droite (Karim 2026-10-24 : « ça sert à rien, il est déjà à droite ») —
           affiché ici seulement sur téléphone et dans le widget intégré. -->
      <div class="mt-4 rounded-2xl bg-gray-50 dark:bg-[#0f0f0f] border border-gray-200 dark:border-[#262626] p-4 space-y-2" :class="estIntegre ? '' : 'lg:hidden'">
        <div class="flex justify-between text-sm">
          <span class="text-[#717198]">{{ $t('adhesion.aslugStepRecap.adhesionAvecParticipants', quantity) }}</span>
          <span class="text-[#2d2e5c] dark:text-white font-medium">{{ formatCurrency(totalPlanPrice) }}</span>
        </div>
        <div v-if="totalOptionsPrice > 0" class="flex justify-between text-sm">
          <span class="text-[#717198]">{{ $t('adhesion.aslugStepRecap.options') }}</span>
          <span class="text-[#2d2e5c] dark:text-white font-medium">{{ formatCurrency(totalOptionsPrice) }}</span>
        </div>
        <div v-if="promoDiscount > 0" class="flex justify-between text-sm">
          <span class="text-[#717198]">{{ $t('adhesion.aslugStepRecap.codePromo', { code: promoApplied?.code }) }}</span>
          <span class="text-emerald-600 dark:text-emerald-400 font-medium">-{{ formatCurrency(promoDiscount) }}</span>
        </div>
        <div v-if="donationAmount > 0" class="flex justify-between text-sm">
          <span class="text-[#717198]">{{ $t('adhesion.aslugStepRecap.donA', { organisation: plan.organization_name || $t('adhesion.aslugStepRecap.organisationParDefaut') }) }}</span>
          <span class="text-[#2d2e5c] dark:text-white font-medium">{{ formatCurrency(donationAmount) }}</span>
        </div>
        <div v-if="fraisAffiches > 0 && includeFees" class="flex justify-between text-sm">
          <span class="text-[#717198]">{{ $t('adhesion.aslugStepRecap.fraisTransaction') }}</span>
          <span class="text-[#2d2e5c] dark:text-white">{{ formatCurrency(fraisAffiches) }}</span>
        </div>
        <div v-if="donateUs && donateToUsAmount > 0" class="flex justify-between text-sm">
          <span class="text-[#717198]">{{ $t('adhesion.aslugStepRecap.soutienDonoor') }}</span>
          <span class="text-[#2d2e5c] dark:text-white">{{ formatCurrency(donateToUsAmount) }}</span>
        </div>
        <div v-if="partsChoisies.length >= 2" class="pt-2 border-t border-gray-200 dark:border-[#262626]">
          <PublicPageTotauxPlusieursFois :parts="partsChoisies" />
        </div>
        <div v-else class="flex justify-between pt-2 border-t border-gray-200 dark:border-[#262626]">
          <span class="text-sm font-semibold text-[#2d2e5c] dark:text-white">{{ (nombreParts ?? 0) >= 2 ? $t('plusieursFois.choix.aPayerAujourdhui') : $t('adhesion.aslugStepRecap.totalAPayer') }}</span>
          <span class="text-lg font-bold text-[#2d2e5c] dark:text-white">
            {{ formatCurrency(aPayerAujourdhui) }}
          </span>
        </div>
      </div>

      <!-- ─── Zone paiement unifiée (Karim 2026-06-21) ─────────────
           Aucun toggle Stripe/PayPal : le provider primaire est imposé
           par `payment_providers.preferred_provider`.
             usesPaypalCard=true  → CardFields PayPal + wallets PayPal
             usesPaypalCard=false → Stripe Elements + wallets Stripe
           + bouton PayPal alternatif si `showPayPalAlternative`. -->
      <div v-if="needsPayment && !requiresApproval && !isPreview" class="mt-4">
        <div
          v-if="stripeInitError && !usesPaypalCard"
          class="rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 p-4 flex items-start gap-3 mb-3"
        >
          <UIcon name="i-lucide-alert-triangle" class="size-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
          <div class="flex-1">
            <p class="text-sm font-semibold text-red-700 dark:text-red-300">{{ $t('adhesion.aslugStepRecap.paiementIndisponible') }}</p>
            <p class="text-xs text-red-600 dark:text-red-400 mt-0.5">{{ stripeErrorMessage }}</p>
            <button
              type="button"
              class="mt-2 text-xs font-semibold text-red-700 dark:text-red-300 underline hover:no-underline"
              @click="$emit('retry-stripe-init')"
            >
              {{ $t('commun.actions.reessayer') }}
            </button>
          </div>
        </div>

        <!-- Express Checkout (wallets primaire + bouton PayPal alt).
             Masqué en plusieurs fois : la 1re part enregistre la carte, ce
             que seul le champ carte ci-dessous fait (comme le don mensuel). -->
        <DonateExpressCheckoutSection
          v-if="(nombreParts ?? 0) < 2"
          :stripe-checkout="stripeCheckout"
          :paypal-checkout="paypalCheckout"
          :payment-form="paymentForm"
          :is-paypal-only-mode="isPaypalOnlyMode"
          :show-pay-pal-alternative="showPayPalAlternative"
          :is-subscription="false"
          :uses-paypal-wallets="usesPaypalWallets"
          paypal-container-id="paypal-alt-buttons-a-page"
        />

        <div v-if="(nombreParts ?? 0) < 2" class="relative my-4 flex items-center">
          <div class="flex-1 border-t border-gray-200 dark:border-[#262626]" />
          <span class="px-3 text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500">{{ $t('adhesion.aslugStepRecap.ouPayerParCarte') }}</span>
          <div class="flex-1 border-t border-gray-200 dark:border-[#262626]" />
        </div>

        <div class="rounded-xl border border-gray-200 dark:border-[#262626] bg-white dark:bg-[#0f0f0f] p-4">
          <DonateCardElement
            :is-initialized="usesPaypalCard ? (paypalCheckout.cardMounted.value || !!paypalCheckout.cardFormError.value) : (stripeCheckout.cardMounted.value || !!stripeCheckout.cardFormError.value)"
            :provider="usesPaypalCard ? 'paypal' : 'stripe'"
            :mount-error-code="
              usesPaypalCard ? (paypalCheckout.cardFormError.value?.code ?? null) : null
            "
          />
        </div>
      </div>
    </template>

    <div v-if="isPreview" class="mt-5 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/50">
      <div class="flex items-center gap-3">
        <UIcon name="i-lucide-eye" class="size-5 text-amber-600 dark:text-amber-400 shrink-0" />
        <p class="text-sm text-amber-800 dark:text-amber-300">{{ $t('adhesion.aslugStepRecap.paiementDesactiveApercu') }}</p>
      </div>
    </div>

    <!-- Conditions : acceptées EN VALIDANT (Karim 2026-09-29), comme la page
         de don — plus de case à cocher ni de boutons de paiement bloqués. -->
    <PublicPageMentionConditions v-if="!isPreview" :paiement="needsPayment && !requiresApproval" />

    <!-- Navigation -->
    <!-- Téléphone : « Payer » sur toute la largeur, « Retour » dessous —
         sur une ligne partagée, le montant passait à la ligne (Karim 2026-10-24). -->
    <div class="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
      <button
        class="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium text-[#505870] dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] transition-colors"
        @click="$emit('back')"
      >
        <UIcon name="i-lucide-arrow-left" class="size-4" />
        {{ $t('commun.actions.retour') }}
      </button>
      <button
        v-if="!isPreview"
        data-testid="btn-submit-order"
        class="w-full sm:w-auto px-6 sm:px-8 py-3 accent-btn rounded-xl text-sm font-semibold hover:bg-[#333] dark:hover:bg-gray-100 transition-colors disabled:opacity-40 flex items-center justify-center gap-2.5"
        :disabled="
          isProcessing ||
          (needsPayment &&
            !requiresApproval &&
            !usesPaypalCard &&
            (!checkoutIsInitialized ||
              stripeInitError ||
              !!checkoutError)) ||
          (needsPayment &&
            !requiresApproval &&
            usesPaypalCard &&
            !paypalCheckout.cardMounted.value)
        "
        @click="$emit('submit')"
      >
        <UIcon v-if="isProcessing" name="i-lucide-loader-2" class="size-4 animate-spin" />
        <UIcon v-else :name="requiresApproval ? 'i-lucide-send' : 'i-lucide-lock'" class="size-4" />
        <span class="flex flex-col items-center sm:items-start leading-tight">
          <span class="whitespace-nowrap text-[15px] sm:text-sm">{{
          isProcessing
            ? $t('adhesion.aslugStepRecap.traitement')
            : requiresApproval
              ? $t('adhesion.aslugStepRecap.envoyerDemande')
              : !needsPayment
                ? $t('adhesion.aslugStepRecap.confirmerAdhesion')
                : suitePaiement ? $t('plusieursFois.choix.payerAujourdhui', { montant: formatCurrency(aPayerAujourdhui) }) : $t('adhesion.aslugStepRecap.payerMontant', { montant: formatCurrency(aPayerAujourdhui) })
        }}</span>
          <span v-if="suitePaiement && !isProcessing && needsPayment" class="mt-0.5 text-xs sm:text-[11px] font-normal opacity-80 whitespace-nowrap">{{ suitePaiement }}</span>
        </span>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Boutons d'action — couleur choisie dans la configuration de l'adhésion.
   Repli #212121 quand aucune couleur n'est posée. Même classe et mêmes
   variables que la billetterie (BslugStepRecap) : une seule règle pour les
   trois domaines. Karim 2026-09-12. */
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
.accent-btn:hover {
  opacity: 0.9;
}
</style>
